import type { Lesson } from '../schema.js';

// Rummy lessons: explain + quiz only, like Bridge's, backgammon's and Whist's. The facts match the game as built
// (packages/game-engine/src/rummy/play.ts and melds.ts), which is basic Rummy for 2 to 4 players: a set is three or
// four cards of one rank in different suits, a run is three or more in a row of one suit with the ace LOW (A-2-3,
// never K-A-2), you draw, lay down melds and lay cards off, then discard one, the first to get rid of every card wins
// the hand and scores what is left in the other hands, and a game goes to 100. The ace-high option exists in the
// engine but the game does not turn it on, so it is not taught. These rules are recalled, not checked against a
// rulebook, and a player should confirm them.
export const RUMMY_LESSONS: Lesson[] = [
  {
    id: 'rummy-melds',
    slug: 'rummy-sets-and-runs',
    title: 'Rummy: sets and runs',
    audienceLevel: 'beginner',
    concepts: ['rummy'],
    steps: [
      {
        kind: 'explain',
        title: 'Two kinds of meld',
        body: 'In Rummy you collect groups of cards called melds. A set is three or four cards of the same number, each from a different suit, like the 7 of hearts, the 7 of clubs and the 7 of spades. A run is three or more cards in a row from one suit, like the 4, 5 and 6 of diamonds. The ace is low: it goes before the 2 in A-2-3, and a run cannot wrap around from king to ace to 2.',
        narration: 'A set is the same number in different suits. A run is a row of numbers in one suit.',
      },
      {
        kind: 'quiz',
        title: 'Melds',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Which group is a set?',
            choices: ['7 of hearts, 7 of clubs, 7 of spades', '4, 5, 6 of diamonds', '7 of hearts, 7 of hearts, 7 of clubs'],
            correct: 0,
          },
          {
            prompt: 'Which group is a run?',
            choices: ['4 of clubs, 5 of hearts, 6 of clubs', '4, 5, 6 of diamonds', '4, 6, 8 of diamonds'],
            correct: 1,
          },
          {
            prompt: 'Which run is allowed, with the ace low?',
            choices: ['Ace, 2, 3 of spades', 'King, ace, 2 of spades', 'Ace, 3, 5 of spades'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'rummy-turn',
    slug: 'rummy-a-turn',
    title: 'Rummy: your turn',
    audienceLevel: 'beginner',
    concepts: ['rummy'],
    steps: [
      {
        kind: 'explain',
        title: 'Draw, meld, discard',
        body: 'Everyone starts with seven cards, or ten when only two people play, and one card is turned up to start the discard pile. On your turn you first draw one card, either the top card of the face-down stock or the top card of the discard pile. Then you may lay melds down on the table, and you may add a card from your hand to any meld on the table, yours or someone else\'s. Finally you discard one card to end your turn. If you took a card from the discard pile, you cannot put that same card straight back. The first player with no cards left wins the hand.',
        narration: 'Draw a card, lay down any melds, then discard one card.',
      },
      {
        kind: 'quiz',
        title: 'A turn',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'What do you do first on your turn?',
            choices: ['Draw a card', 'Discard a card', 'Lay down a meld'],
            correct: 0,
          },
          {
            prompt: 'You may add one of your cards to a meld that...',
            choices: ['Only you laid down', 'Is anywhere on the table', 'You have not seen yet'],
            correct: 1,
          },
          {
            prompt: 'You took the top card of the discard pile. Can you discard that same card straight away?',
            choices: ['Yes', 'No, you must discard a different card'],
            correct: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'rummy-scoring',
    slug: 'rummy-scoring-a-game',
    title: 'Rummy: scoring a game',
    audienceLevel: 'beginner',
    concepts: ['rummy'],
    steps: [
      {
        kind: 'explain',
        title: 'Points for going out',
        body: 'When someone gets rid of all their cards, the hand is over. The winner scores the value of the cards still in the other players\' hands. A face card (jack, queen, king) is 10, an ace is 1, and every other card is worth its number. The points add up over several hands, and the first player to reach 100 wins the game. If the face-down stock runs out, the discard pile is turned over to make a new one, but only three times. After that, if nobody can go out, the hand stops and the player with the lowest hand wins it.',
        narration: 'Going out scores the cards left in the other hands. First to one hundred wins.',
      },
      {
        kind: 'quiz',
        title: 'Scoring',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'What is a king worth when it is left in a hand?',
            choices: ['13', '10', '1'],
            correct: 1,
          },
          {
            prompt: 'You go out. An opponent still holds a 9 and an ace. How many points do you score from that hand?',
            choices: ['10', '9', '1'],
            correct: 0,
          },
          {
            prompt: 'How many points does a player need to win the game?',
            choices: ['50', '100', '500'],
            correct: 1,
          },
        ],
      },
    ],
  },
];
